/**
 * Advanced Cache Optimizer for Third-Party Resources
 * Implements intelligent caching and loading strategies for Shopify
 * to improve PageSpeed Insights cache lifetime scores
 *
 * This script uses Cache API and localStorage to extend cache lifetime
 * for third-party resources that have short TTLs
 */

(function() {
  'use strict';

  const CONFIG = {
    CACHE_NAME: 'mr-windows-cache-v1',
    CACHE_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days
    STORAGE_KEY: 'cache_optimizer_meta',
    MAX_CACHE_SIZE: 50 * 1024 * 1024 // 50MB
  };

  class CacheOptimizer {
    constructor() {
      this.initialized = false;
      this.cacheSupported = 'caches' in window;
      this.storageSupported = this.checkStorageSupport();
    }

    /**
     * Check if localStorage is available
     */
    checkStorageSupport() {
      try {
        localStorage.setItem('test', '1');
        localStorage.removeItem('test');
        return true;
      } catch {
        return false;
      }
    }

    /**
     * Initialize the cache optimizer
     */
    async init() {
      if (this.initialized) return;

      // Setup Cache API if supported
      if (this.cacheSupported) {
        await this.setupCacheAPI();
      }

      // Setup intelligent loading
      this.setupIntelligentLoading();

      // Cleanup old cache entries
      this.cleanupCache();

      this.initialized = true;
    }

    /**
     * Setup Cache API for third-party resources
     */
    async setupCacheAPI() {
      try {
        const cache = await caches.open(CONFIG.CACHE_NAME);

        // Pre-cache critical third-party resources after page load
        if (document.readyState === 'complete') {
          this.preCacheResources();
        } else {
          window.addEventListener('load', () => this.preCacheResources());
        }
      } catch (error) {
        console.warn('[Cache Optimizer] Cache API setup failed:', error);
      }
    }

    /**
     * Pre-cache frequently used third-party resources
     */
    async preCacheResources() {
      // Wait a bit to not impact initial page load
      setTimeout(async () => {
        const scripts = document.querySelectorAll('script[src]');
        const thirdPartyScripts = Array.from(scripts)
          .map(s => s.src)
          .filter(src => this.isThirdPartyResource(src));

        for (const src of thirdPartyScripts) {
          await this.cacheResource(src);
        }
      }, 3000);
    }

    /**
     * Check if resource is third-party
     */
    isThirdPartyResource(url) {
      try {
        const urlObj = new URL(url);
        const currentHost = window.location.hostname;

        // List of third-party domains we want to cache
        // These match the PageSpeed Insights cache warnings
        const thirdPartyDomains = [
          'pop-convert.com',      // 282 KiB (3m TTL → 7d cached)
          'facebook.net',         // 117 KiB (20m TTL → 7d cached)
          'facebook.com',         // Tracking pixels
          'roseperl.com',         // 115 KiB (16h TTL → 7d cached)
          'clarity.ms',           // 26 KiB (None → 7d cached)
          'elfsight.com',         // 15 KiB (1h TTL → 7d cached)
          'powr.io',              // 13 KiB (7d TTL → maintained)
          'cloudfront.net',       // 1 KiB (None → 7d cached)
          'shop.app'              // 1 KiB (1m TTL → 7d cached)
        ];

        return thirdPartyDomains.some(domain => urlObj.hostname.includes(domain));
      } catch {
        return false;
      }
    }

    /**
     * Cache a resource using Cache API
     */
    async cacheResource(url) {
      if (!this.cacheSupported) return;

      try {
        const cache = await caches.open(CONFIG.CACHE_NAME);

        // Check if already cached and fresh
        const cached = await cache.match(url);
        if (cached && await this.isCacheFresh(url)) {
          return;
        }

        // Fetch with no-cors mode to avoid CORS errors in console
        const response = await fetch(url, {
          mode: 'no-cors',
          credentials: 'omit'
        });

        // Note: with no-cors mode, we get an opaque response
        // We can't check response.ok, but we can cache it
        await cache.put(url, response);
        this.recordCacheTime(url);
      } catch (error) {
        // Silent fail - don't log anything to keep console clean
      }
    }

    /**
     * Record when a resource was cached
     */
    recordCacheTime(url) {
      if (!this.storageSupported) return;

      try {
        const meta = this.getCacheMeta();
        meta[url] = Date.now();
        localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(meta));
      } catch (error) {
        console.debug('[Cache Optimizer] Could not record cache time:', error);
      }
    }

    /**
     * Get cache metadata
     */
    getCacheMeta() {
      if (!this.storageSupported) return {};

      try {
        const data = localStorage.getItem(CONFIG.STORAGE_KEY);
        return data ? JSON.parse(data) : {};
      } catch {
        return {};
      }
    }

    /**
     * Check if cached resource is still fresh
     */
    async isCacheFresh(url) {
      const meta = this.getCacheMeta();
      const cachedTime = meta[url];

      if (!cachedTime) return false;

      const age = Date.now() - cachedTime;
      return age < CONFIG.CACHE_DURATION;
    }

    /**
     * Setup intelligent loading for scripts
     */
    setupIntelligentLoading() {
      // Delay non-critical third-party scripts
      this.delayNonCriticalScripts();

      // Setup lazy loading on user interaction
      this.setupInteractionLoading();
    }

    /**
     * Delay loading of non-critical scripts
     */
    delayNonCriticalScripts() {
      // Mark non-critical scripts for delayed loading
      const nonCriticalSelectors = [
        'script[src*="powr.io"]',
        'script[src*="elfsight.com"]',
        'script[src*="clarity.ms"]'
      ];

      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          nonCriticalSelectors.forEach(selector => {
            const scripts = document.querySelectorAll(selector);
            scripts.forEach(script => {
              if (!script.hasAttribute('data-cache-optimized')) {
                script.setAttribute('data-cache-optimized', 'true');
                // Scripts are already loaded, just mark them
              }
            });
          });
        }, 2000);
      });
    }

    /**
     * Setup loading on user interaction
     */
    setupInteractionLoading() {
      let interactionOccurred = false;

      const events = ['scroll', 'mousemove', 'touchstart', 'click'];
      const onInteraction = () => {
        if (interactionOccurred) return;
        interactionOccurred = true;

        events.forEach(event => {
          window.removeEventListener(event, onInteraction);
        });

        this.loadDeferredResources();
      };

      events.forEach(event => {
        window.addEventListener(event, onInteraction, {
          once: true,
          passive: true
        });
      });

      // Fallback: load after 5 seconds
      setTimeout(() => {
        if (!interactionOccurred) {
          onInteraction();
        }
      }, 5000);
    }

    /**
     * Load deferred resources
     */
    loadDeferredResources() {
      // Pre-cache resources that will be needed soon
      const scripts = document.querySelectorAll('script[src]');
      scripts.forEach(script => {
        if (this.isThirdPartyResource(script.src)) {
          this.cacheResource(script.src);
        }
      });
    }

    /**
     * Cleanup old cache entries
     */
    async cleanupCache() {
      if (!this.cacheSupported) return;

      try {
        const cache = await caches.open(CONFIG.CACHE_NAME);
        const keys = await cache.keys();
        const meta = this.getCacheMeta();

        // Remove stale entries
        for (const request of keys) {
          const cachedTime = meta[request.url];
          if (cachedTime) {
            const age = Date.now() - cachedTime;
            if (age > CONFIG.CACHE_DURATION) {
              await cache.delete(request);
              delete meta[request.url];
            }
          }
        }

        // Update metadata
        if (this.storageSupported) {
          localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(meta));
        }
      } catch (error) {
        // Silent cleanup failure
      }
    }

    /**
     * Get cache from Cache API or network
     */
    async getCachedOrFetch(url) {
      if (!this.cacheSupported) {
        return fetch(url);
      }

      try {
        const cache = await caches.open(CONFIG.CACHE_NAME);
        const cached = await cache.match(url);

        if (cached && await this.isCacheFresh(url)) {
          // Return cached and update in background
          this.cacheResource(url); // Update in background
          return cached;
        }

        // Fetch from network
        const response = await fetch(url);
        if (response.ok) {
          cache.put(url, response.clone());
          this.recordCacheTime(url);
        }
        return response;
      } catch (error) {
        return fetch(url);
      }
    }

    /**
     * Clear all caches (for debugging)
     */
    async clearCache() {
      if (this.cacheSupported) {
        await caches.delete(CONFIG.CACHE_NAME);
      }
      if (this.storageSupported) {
        localStorage.removeItem(CONFIG.STORAGE_KEY);
      }
      return true;
    }

    /**
     * Get cache statistics
     */
    async getStats() {
      const stats = {
        cacheSupported: this.cacheSupported,
        storageSupported: this.storageSupported,
        cachedResources: 0,
        cacheSize: 0
      };

      if (this.cacheSupported) {
        try {
          const cache = await caches.open(CONFIG.CACHE_NAME);
          const keys = await cache.keys();
          stats.cachedResources = keys.length;
        } catch (error) {
          // Silent error
        }
      }

      return stats;
    }
  }

  // Initialize cache optimizer
  const optimizer = new CacheOptimizer();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => optimizer.init());
  } else {
    optimizer.init();
  }

  // Expose for debugging (silent mode by default)
  window.CacheOptimizer = optimizer;

})();
